import React from 'react';
import { useTranslation } from 'react-i18next';

export interface WizardStepperStep {
  key: string;
  title: string;
  subtitle?: string;
  /** Marks a section edited in edit mode ("Ändrad"). */
  changed?: boolean;
}

interface WizardStepperProps {
  steps: WizardStepperStep[];
  /** Index of the active step. */
  currentStep: number;
  /** Highest step index the user may jump to (visited steps; all steps in edit mode). */
  maxReachableStep: number;
  /** In edit mode steps are sections, not a sequence: none is shown as "done". */
  mode?: 'create' | 'edit';
  onStepClick: (stepIndex: number) => void;
}

/**
 * Step indicator for the contract wizard. Desktop shows every step with number,
 * title and subtitle; below `md` a compact "Steg X av N" line with a segmented
 * progress bar is shown instead. Visited steps are buttons so the user can move
 * back and forth freely; the active step carries `aria-current="step"`.
 */
export function WizardStepper({ steps, currentStep, maxReachableStep, mode = 'create', onStepClick }: WizardStepperProps) {
  const { t } = useTranslation();

  const stepState = (index: number) => {
    const current = index === currentStep;
    const done = mode === 'create' && index < currentStep;
    const reachable = !current && index <= maxReachableStep;
    return { current, done, reachable };
  };

  const stepAria = (step: WizardStepperStep, index: number) => {
    const { current, done } = stepState(index);
    const status = current
      ? t('contracts.wizard.stepper.statusCurrent')
      : done
        ? t('contracts.wizard.stepper.statusDone')
        : null;
    const label = status
      ? t('contracts.wizard.stepper.stepAriaWithStatus', { number: index + 1, title: step.title, status })
      : t('contracts.wizard.stepper.stepAria', { number: index + 1, title: step.title });
    return step.changed ? t('contracts.wizard.stepper.stepAriaChanged', { label }) : label;
  };

  const next = steps[currentStep + 1];

  return (
    <nav className="wizard-stepper" aria-label={t('contracts.wizard.stepper.ariaLabel')}>
      <ol className="wizard-stepper-list d-none d-md-flex">
        {steps.map((step, index) => {
          const { current, done, reachable } = stepState(index);
          return (
            <li
              key={step.key}
              className={`wizard-stepper-item${index < steps.length - 1 ? ' has-line' : ''}${done ? ' is-done' : ''}`}
            >
              <button
                type="button"
                className={`wizard-stepper-button${current ? ' is-current' : ''}${done ? ' is-done' : ''}${reachable ? ' is-reachable' : ''}`}
                onClick={() => reachable && onStepClick(index)}
                disabled={!reachable}
                aria-current={current ? 'step' : undefined}
                aria-label={stepAria(step, index)}
              >
                <span className="wizard-stepper-circle" aria-hidden="true">
                  {done ? <i className="feather-check"></i> : index + 1}
                </span>
                <span className="wizard-stepper-text" aria-hidden="true">
                  <span className="wizard-stepper-title">
                    {step.title}
                    {step.changed && (
                      <span className="wizard-changed-badge">{t('contracts.wizard.stepper.changed')}</span>
                    )}
                  </span>
                  {step.subtitle && <span className="wizard-stepper-subtitle">{step.subtitle}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <div className="wizard-stepper-compact d-md-none">
        <div className="d-flex justify-content-between align-items-baseline gap-2 small">
          <span className="wizard-stepper-counter">
            {t('contracts.wizard.stepper.counter', { current: currentStep + 1, total: steps.length })}
          </span>
          <span className="text-muted">
            {next
              ? t('contracts.wizard.stepper.nextHint', { title: next.title })
              : t('contracts.wizard.stepper.lastStep')}
          </span>
        </div>
        <ol className="wizard-stepper-segments">
          {steps.map((step, index) => {
            const { current, done, reachable } = stepState(index);
            return (
              <li key={step.key} className="flex-fill">
                <button
                  type="button"
                  className={`wizard-stepper-segment${current ? ' is-current' : ''}${done ? ' is-done' : ''}${step.changed ? ' is-changed' : ''}`}
                  onClick={() => reachable && onStepClick(index)}
                  disabled={!reachable}
                  aria-current={current ? 'step' : undefined}
                  aria-label={stepAria(step, index)}
                ></button>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}

export default WizardStepper;
